/**
 * Constantes compartilhadas do sistema
 * Centralização para evitar duplicação de código
 */

// Labels de status de etapas de produção
export const STATUS_LABELS = {
  'PROGRAMADO': 'Programado',
  'EM_EXECUCAO': 'Em Execução',
  'PAUSADO': 'Pausado',
  'CONCLUIDO': 'Concluído'
} as const;

// Labels de status de pedidos
// Status canônicos do pedido (CLAUDE.md §8): PENDENTE_PROGRAMACAO → EM_PRODUCAO → PRONTO (legado) → CONCLUIDO.
export const ORDER_STATUS_LABELS = {
  'PENDENTE_PROGRAMACAO': 'Pendente de programação',
  'PENDENTE': 'Pendente',
  'EM_PRODUCAO': 'Em Produção',
  'PRONTO': 'Pronto',
  'CONCLUIDO': 'Concluído',
  'CANCELADO': 'Cancelado'
} as const;

// Cores de prioridade (para Admin/Manager)
export const PRIORIDADE_COLORS = {
  'URGENTE': 'bg-red-500 text-white',
  'ALTA': 'bg-orange-500 text-white',
  'NORMAL': 'bg-blue-500 text-white'
} as const;

// Cores de prioridade para Operadores (variante simplificada)
export const PRIORIDADE_COLORS_OPERATOR = {
  'URGENTE': 'bg-destructive text-white',
  'ALTA': 'bg-orange-500 text-white',
  'NORMAL': 'bg-primary text-white'
} as const;

// Cores de status de etapas
export const STATUS_COLORS = {
  'PROGRAMADO': 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  'EM_EXECUCAO': 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20',
  'PAUSADO': 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  'CONCLUIDO': 'bg-green-500/10 text-green-700 border-green-500/20',
  'CANCELADO': 'bg-red-500/10 text-red-700 border-red-500/20'
} as const;

/**
 * As cores do CAMINHO no estoque, nas compras e na separação (REGRAS §13.45):
 * azul = a fazer · âmbar = em andamento/atenção · verde = feito · vermelho =
 * falta, erro ou atraso · azul-piscina = vem do corte. Uma tela nova pega a
 * cor DAQUI — não inventa a sua. `chip` é o selinho, `cabecalho` o topo de
 * coluna, `caixa` o bloco/linha inteiro, `texto` só a cor da letra.
 */
export type TomDoCaminho = 'fazer' | 'andamento' | 'feito' | 'falta' | 'corte';
export const TOM_DO_CAMINHO: Record<TomDoCaminho, { chip: string; cabecalho: string; caixa: string; texto: string }> = {
  fazer: {
    chip: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
    cabecalho: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
    caixa: 'border-blue-300 bg-blue-50/60 dark:border-blue-800 dark:bg-blue-950/20',
    texto: 'text-blue-700 dark:text-blue-300',
  },
  andamento: {
    chip: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    cabecalho: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    caixa: 'border-amber-300 bg-amber-50/70 dark:border-amber-800 dark:bg-amber-950/20',
    texto: 'text-amber-800 dark:text-amber-300',
  },
  feito: {
    chip: 'border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300',
    cabecalho: 'border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300',
    caixa: 'border-green-300 bg-green-50/70 dark:border-green-800 dark:bg-green-950/20',
    texto: 'text-green-700 dark:text-green-300',
  },
  falta: {
    chip: 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300',
    cabecalho: 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300',
    caixa: 'border-red-300 bg-red-50/70 dark:border-red-800 dark:bg-red-950/20',
    texto: 'text-red-700 dark:text-red-300',
  },
  corte: {
    chip: 'border-cyan-300 bg-cyan-50 text-cyan-800 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300',
    cabecalho: 'border-cyan-300 bg-cyan-50 text-cyan-800 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300',
    caixa: 'border-cyan-300 bg-cyan-50/70 dark:border-cyan-800 dark:bg-cyan-950/20',
    texto: 'text-cyan-800 dark:text-cyan-300',
  },
};

/**
 * Classe da data sugerida no Programar, contra a data-meta (rótulo na língua da
 * fábrica + cor). Verde = dentro da meta · amarelo claro = aceitável · amarelo
 * forte = abaixo da meta ou fecha no dia · vermelho = não fecha. `chip` é o
 * selinho/caixa do topo do painel; o texto passa de 4,5:1 nos dois temas.
 */
export type ClasseDeMetaProgramacao = 'CABE' | 'CABE_FOLGA_MENOR' | 'ACIMA_DO_TIPICO' | 'DENTRO_DA_META' | 'ACEITAVEL' | 'FECHA_NO_DIA' | 'ABAIXO_DA_META' | 'NAO_FECHA' | 'ACIMA_DA_CAPACIDADE';
export const CLASSE_DE_META: Record<ClasseDeMetaProgramacao, { rotulo: string; caixa: string }> = {
  // Motor por capacidade (capacidade-v1): mesmas caixas das classes da fila que mais se parecem.
  CABE: {
    rotulo: 'CABE',
    caixa: TOM_DO_CAMINHO.feito.caixa + ' text-green-800 dark:text-green-200',
  },
  CABE_FOLGA_MENOR: {
    rotulo: 'CABE COM FOLGA MENOR',
    caixa: 'border-yellow-300 bg-yellow-50 text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-200',
  },
  ACIMA_DO_TIPICO: {
    rotulo: 'ACIMA DO TÍPICO',
    caixa: 'border-amber-500 bg-amber-200 text-amber-950 dark:border-amber-500 dark:bg-amber-900/60 dark:text-amber-50',
  },
  DENTRO_DA_META: {
    rotulo: 'DENTRO DA META',
    caixa: TOM_DO_CAMINHO.feito.caixa + ' text-green-800 dark:text-green-200',
  },
  ACEITAVEL: {
    rotulo: 'ACEITÁVEL',
    caixa: 'border-yellow-300 bg-yellow-50 text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-200',
  },
  FECHA_NO_DIA: {
    rotulo: 'FECHA NO DIA',
    caixa: 'border-amber-500 bg-amber-200 text-amber-950 dark:border-amber-500 dark:bg-amber-900/60 dark:text-amber-50',
  },
  ABAIXO_DA_META: {
    rotulo: 'ABAIXO DA META',
    caixa: 'border-amber-500 bg-amber-200 text-amber-950 dark:border-amber-500 dark:bg-amber-900/60 dark:text-amber-50',
  },
  NAO_FECHA: {
    rotulo: 'NÃO FECHA',
    caixa: TOM_DO_CAMINHO.falta.caixa + ' text-red-800 dark:text-red-200',
  },
  ACIMA_DA_CAPACIDADE: {
    rotulo: 'ACIMA DA CAPACIDADE',
    caixa: TOM_DO_CAMINHO.falta.caixa + ' text-red-800 dark:text-red-200',
  },
};

// Mapeamento de setores para display
export const SECTOR_LABELS = {
  'impressao': 'Impressão',
  'acabamento': 'Acabamento',
  'expedicao': 'Expedição'
} as const;

// Setores que NÃO bloqueiam expedição (são paralelos à produção principal)
// EXPEDICAO = próprio setor destino, IMPRESSAO = fluxo paralelo
export const NON_BLOCKING_SECTORS: readonly string[] = ['EXPEDICAO', 'IMPRESSAO'] as const;

// Status do Trello que representam estágios finais (exclusão mútua no Kanban Arte)
export const FINAL_STAGE_TRELLO_STATUSES = [
  'pedido pronto',
  'pagamento final'
] as const;

// Status do Trello que representam estágios iniciais (antes da produção)
export const INITIAL_STAGE_TRELLO_STATUSES = [
  'prévia',
  'previa',
  'montar layout',
  'confirmação',
  'aguardando',
  'aprovação',
  'falta'
] as const;

// Labels de status de logística
export const LOGISTICS_STATUS_LABELS = {
  'pending': 'Aguardando Cotação',
  'quoted': 'Cotado',
  'dispatched': 'Despachado',
  'in_transit': 'Em Trânsito',
  'delivered': 'Entregue',
  'cancelled': 'Cancelado'
} as const;

// Cores de status de logística (semantic tokens)
export const LOGISTICS_STATUS_COLORS = {
  'pending': 'bg-muted text-muted-foreground',
  'quoted': 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  'dispatched': 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20',
  'in_transit': 'bg-orange-500/10 text-orange-700 border-orange-500/20',
  'delivered': 'bg-green-500/10 text-green-700 border-green-500/20',
  'cancelled': 'bg-destructive/10 text-destructive border-destructive/20'
} as const;

// Cores de borda para colunas do Kanban
export const KANBAN_COLUMN_BORDERS = {
  'pending': 'border-l-4 border-l-yellow-500',
  'quoted': 'border-l-4 border-l-blue-500',
  'dispatched': 'border-l-4 border-l-purple-500',
  'in_transit': 'border-l-4 border-l-orange-500',
  'delivered': 'border-l-4 border-l-green-500',
  'cancelled': 'border-l-4 border-l-red-500'
} as const;

export type StatusLabel = keyof typeof STATUS_LABELS;
export type OrderStatusLabel = keyof typeof ORDER_STATUS_LABELS;
export type PrioridadeLabel = keyof typeof PRIORIDADE_COLORS;
export type StatusColor = keyof typeof STATUS_COLORS;
export type SectorLabel = keyof typeof SECTOR_LABELS;
export type LogisticsStatusLabel = keyof typeof LOGISTICS_STATUS_LABELS;
export type LogisticsStatusColor = keyof typeof LOGISTICS_STATUS_COLORS;

// =====================================================
// SISTEMA MULTI-SETOR E MULTI-MÓDULO
// =====================================================

/**
 * Grupos de setores para exibição organizada no cadastro de usuários
 * Elimina duplicação de lógica de agrupamento em UserManagement.tsx
 */
export const SECTOR_GROUPS = {
  arte: { 
    label: '🎨 Arte e Comercial', 
    sectors: ['ARTE', 'COMERCIAL'] 
  },
  logistica: { 
    label: '🚚 Logística', 
    sectors: ['LOGISTICA'] 
  },
  corte: { 
    label: '✂️ Corte e Costura', 
    sectors: ['CORTE', 'COSTURA'] 
  },
  estamparia: { 
    label: '🖌️ Estamparia', 
    sectors: ['SILK', 'DTF', 'SUBLIMACAO', 'BORDADO', 'IMPRESSAO'] 
  },
  suporte: { 
    label: '🔧 Suporte', 
    sectors: ['SAIDA_ESTOQUE', 'COMPRAS'] 
  },
  expedicao: { 
    label: '📦 Expedição', 
    sectors: ['EXPEDICAO'] 
  },
  // Grupo PRÓPRIO, e não dentro de 'suporte', de propósito: o SectorSwitcher
  // deriva PRODUCTION_ONLY_SECTORS de corte+estamparia+suporte+expedicao, então
  // ESTOQUE ali dentro faria o switcher aparecer para todo cortador que
  // acumulasse o setor — e trocar para ESTOQUE gravaria setor_ativo, mandando o
  // login seguinte para /estoquista. ESTOQUE é posto de bipagem, não etapa de
  // produção: existe aqui só para o cadastro de usuário poder atribuí-lo.
  estoque: {
    label: '📥 Estoque',
    sectors: ['ESTOQUE']
  }
} as const;

export type SectorGroupKey = keyof typeof SECTOR_GROUPS;

/** Helper: Lista plana de todos os setores */
export const ALL_SECTORS = Object.values(SECTOR_GROUPS).flatMap(g => g.sectors);

